/**
 * Nutrition Calculator Utilities
 * Extracted from NutritionTracker.js for better testability
 */
import { pausedCutDays, formatLocalDate } from './vacation';

// Macro totals interface
export interface MacroTotals {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

// Food entry interface for macro calculation
export interface FoodEntryForMacros {
  calories?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
}

// Per 100g nutrition interface
export interface NutritionPer100g {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

/**
 * Calculate BMR using Mifflin-St Jeor Equation
 * @param weightKg - Weight in kilograms
 * @param heightCm - Height in centimeters
 * @param age - Age in years
 * @param gender - 'male' or 'female'
 * @returns BMR in calories
 */
export function calculateBMR(
  weightKg: number,
  heightCm: number,
  age: number,
  gender: string
): number {
  if (weightKg <= 0 || heightCm <= 0 || age <= 0) {
    return 0;
  }
  
  if (gender === 'male') {
    return Math.round((10 * weightKg) + (6.25 * heightCm) - (5 * age) + 5);
  } else {
    return Math.round((10 * weightKg) + (6.25 * heightCm) - (5 * age) - 161);
  }
}

/**
 * Calculate TDEE (Total Daily Energy Expenditure)
 * @param bmr - Basal Metabolic Rate
 * @param activityFactor - Activity multiplier (1.2 sedentary, 1.375 light, 1.55 moderate, 1.725 active, 1.9 very active)
 * @returns TDEE in calories
 */
export function calculateTDEE(bmr: number, activityFactor: number = 1.2): number {
  return Math.round(bmr * activityFactor);
}

/**
 * Convert weight between units
 * @param weight - Weight value
 * @param fromUnit - Source unit ('kg' or 'lbs')
 * @param toUnit - Target unit ('kg' or 'lbs')
 * @returns Converted weight
 */
export function convertWeight(weight: number, fromUnit: string, toUnit: string): number {
  if (fromUnit === toUnit) return weight;
  
  if (fromUnit === 'lbs' && toUnit === 'kg') {
    return weight * 0.453592;
  } else if (fromUnit === 'kg' && toUnit === 'lbs') {
    return weight / 0.453592;
  }
  return weight;
}

/**
 * Convert height between units
 * @param height - Height value
 * @param fromUnit - Source unit ('cm' or 'in')
 * @param toUnit - Target unit ('cm' or 'in')
 * @returns Converted height
 */
export function convertHeight(height: number, fromUnit: string, toUnit: string): number {
  if (fromUnit === toUnit) return height;
  
  if (fromUnit === 'in' && toUnit === 'cm') {
    return height * 2.54;
  } else if (fromUnit === 'cm' && toUnit === 'in') {
    return height / 2.54;
  }
  return height;
}

/**
 * Calculate weight goal calorie adjustment
 * @param goal - 'lose', 'gain', 'maintain', or 'mini_cut'
 * @param weekNumber - Current week in the program
 * @param isRelaxDay - Whether it's a relax day (Sunday for weight loss)
 * @param opts - Optional context for percentage-based goals (mini_cut).
 * @returns Calorie adjustment (negative for deficit, positive for surplus)
 */
export function calculateGoalAdjustment(
  goal: string,
  weekNumber: number,
  isRelaxDay: boolean = false,
  opts?: { baseTdee?: number; miniCutWeeks?: 2 | 4 }
): number {
  if (goal === 'maintain' || weekNumber <= 0) {
    return 0;
  }

  // Iter243: Mini-Cut — a short (2 or 4-week) aggressive cut followed by
  // a 2-week -17.5% recovery, then maintain. Deficits are computed as a
  // percentage of baseTdee, so the numbers scale with the user's actual
  // maintenance (whether static or adaptive).
  //   2-week variant: W1-2 -35% · W3-4 -17.5% · W5+ 0
  //   4-week variant: W1-4 -35% · W5-6 -17.5% · W7+ 0
  // Aggressive-cut weeks ignore the Sunday relax rule — the deficit is
  // already time-boxed and short enough that a mid-cut break defeats it.
  // Iter252ar (Feb 2026): user requested steeper cut — bumped aggressive
  // 30% → 35% and recovery 15% → 17.5% across both variants.
  if (goal === 'mini_cut') {
    const base = opts?.baseTdee ?? 0;
    if (base <= 0) return 0;
    const dur = opts?.miniCutWeeks === 4 ? 4 : 2; // default 2
    if (weekNumber <= dur) return -Math.round(base * 0.35);
    if (weekNumber <= dur + 2) return -Math.round(base * 0.175);
    return 0;
  }

  // Iter223: moderate weight-loss corrected to the -300/-600 track per
  // the user (Personal → Body Weight → Lose Weight):
  //   Weeks 1-2: -300 · Weeks 3-10: -600 (Sun relax: -300) · Weeks 11-12: -300
  // `lose_fast` kept as a UI alias (identical numbers) so old profiles
  // storing "lose_fast" keep working.
  if (goal === 'lose' || goal === 'lose_fast') {
    if (weekNumber <= 2) return -300;
    if (weekNumber <= 10) return isRelaxDay ? -300 : -600;
    if (weekNumber <= 12) return -300;
    return 0;
  }

  // Both gain paces share +250 (user opted for parity, iter222).
  if (goal === 'gain' || goal === 'gain_fast') {
    return weekNumber <= 12 ? 250 : 0;
  }

  return 0;
}

/**
 * SINGLE SOURCE OF TRUTH for goal-week + Sunday-relax detection + deficit
 * computation (iter226 — fixes cross-file drift and the timezone bug where
 * users west of UTC applied the relax deficit on the wrong calendar day).
 *
 * Rules:
 * - Dates are parsed as **LOCAL** midnight (`YYYY-MM-DD` → local zone) so
 *   `getDay()` returns the user's own weekday, not UTC's. Previously we
 *   used `new Date('2026-02-15')` which is UTC midnight and slid to
 *   Saturday for anyone in the Americas — the Sunday −300 relief landed
 *   on Monday for them.
 * - Malformed inputs return a neutral result (0, 0, false) so callers
 *   render "maintain" instead of crashing.
 *
 * Used by: nutritionGoals.ts, useBMRCalculation.ts, bmrUtils.ts
 * (computeBaseTdeeAndAdjustment + calculateDashboardNutrition).
 */
export interface GoalAdjustmentResult {
  calorieAdjustment: number;
  goalWeek: number;
  isRelaxDay: boolean;
}

export function computeCalorieAdjustment(
  weightGoal: string | null | undefined,
  goalStart: string | null | undefined,
  dateKey: string | null | undefined,
  opts?: { baseTdee?: number; miniCutWeeks?: 2 | 4 },
): GoalAdjustmentResult {
  const goal = String(weightGoal || 'maintain').toLowerCase();
  if (goal === 'maintain' || !goalStart) {
    return { calorieAdjustment: 0, goalWeek: 0, isRelaxDay: false };
  }
  // Parse as LOCAL midnight — `'YYYY-MM-DD'` alone is treated as UTC
  // midnight by the JS spec; appending 'T00:00:00' switches to local.
  const startDate = new Date(String(goalStart).slice(0, 10) + 'T00:00:00');
  const currentDate = dateKey
    ? new Date(String(dateKey).slice(0, 10) + 'T00:00:00')
    : new Date();
  if (!Number.isFinite(startDate.getTime()) || !Number.isFinite(currentDate.getTime())) {
    return { calorieAdjustment: 0, goalWeek: 0, isRelaxDay: false };
  }
  const daysDiff = Math.floor(
    (currentDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
  );
  // iter364 — Vacation Mode, "make a break": days spent away never counted as
  // diet days, so the cut clock must not have advanced through them. Without
  // this, a two-week holiday silently eats two weeks of a Mini-Cut and the user
  // comes home to maintenance macros under a badge that still says Mini-Cut.
  const paused = pausedCutDays(goalStart, formatLocalDate(currentDate));
  const goalWeek = Math.floor(Math.max(0, daysDiff - paused) / 7) + 1;
  const isRelaxDay = currentDate.getDay() === 0; // 0 = Sunday, local
  const calorieAdjustment = calculateGoalAdjustment(goal, goalWeek, isRelaxDay, opts);
  return { calorieAdjustment, goalWeek, isRelaxDay };
}

/**
 * iter350 — a goal's deficit is time-boxed, and when the box runs out the
 * adjustment silently becomes 0, i.e. maintenance macros under a badge that
 * still says "Mini-Cut". Switching goals also kept the OLD start date (the UI
 * only set it when none existed), so picking Mini-Cut after weeks of Lose
 * started it at "week 12" — finished before it began. This describes the phase
 * so the state is visible instead of silent.
 */
export function describeGoalPhase(
  goal: string | null | undefined,
  goalWeek: number,
  miniCutWeeks: 2 | 4 = 2,
): { label: string; finished: boolean } | null {
  const g = String(goal || 'maintain').toLowerCase();
  if (g === 'maintain' || !goalWeek || goalWeek <= 0) return null;

  const lastWeek = g === 'mini_cut' ? miniCutWeeks + 2 : 12;
  if (goalWeek > lastWeek) {
    return { label: `Week ${goalWeek} · finished`, finished: true };
  }
  if (g === 'mini_cut') {
    const pct = goalWeek <= miniCutWeeks ? '-35%' : '-17.5%';
    return { label: `Week ${goalWeek} of ${lastWeek} · ${pct}`, finished: false };
  }
  return { label: `Week ${goalWeek} of ${lastWeek}`, finished: false };
}

/**
 * Calculate macro nutrients from portion size
 * @param per100g - Nutrition per 100g { calories, protein, carbs, fat }
 * @param portionGrams - Portion size in grams
 * @returns Calculated macros for portion
 */
export function calculatePortionMacros(per100g: NutritionPer100g, portionGrams: number): MacroTotals {
  const multiplier = portionGrams / 100;
  return {
    calories: Math.round(per100g.calories * multiplier),
    protein: Math.round(per100g.protein * multiplier * 10) / 10,
    carbs: Math.round(per100g.carbs * multiplier * 10) / 10,
    fat: Math.round(per100g.fat * multiplier * 10) / 10
  };
}

/**
 * Calculate total macros from food entries
 * @param entries - Array of food entries
 * @returns Total macros { calories, protein, carbs, fat }
 */
export function calculateTotalMacros(entries: FoodEntryForMacros[]): MacroTotals {
  const initial: MacroTotals = { calories: 0, protein: 0, carbs: 0, fat: 0 };
  return entries.reduce<MacroTotals>((totals, entry) => ({
    calories: totals.calories + (entry.calories || 0),
    protein: totals.protein + (entry.protein || 0),
    carbs: totals.carbs + (entry.carbs || 0),
    fat: totals.fat + (entry.fat || 0)
  }), initial);
}

/**
 * Calculate workout calories burned (estimate)
 * @param durationMinutes - Workout duration in minutes
 * @param intensity - 'low', 'medium', or 'high'
 * @returns Estimated calories burned
 */
export function calculateWorkoutCalories(durationMinutes: number, intensity: string = 'medium'): number {
  const caloriesPerHour: Record<string, number> = {
    low: 250,
    medium: 350,
    high: 500
  };
  
  const rate = caloriesPerHour[intensity] || caloriesPerHour.medium;
  return Math.round((durationMinutes / 60) * rate);
}

/**
 * Calculate walking calories burned
 * @param steps - Number of steps
 * @param strideLength - Stride length in meters
 * @param weightKg - Body weight in kg
 * @returns Estimated calories burned
 */
export function calculateWalkingCalories(
  steps: number,
  strideLength: number = 0.75,
  weightKg: number = 70
): number {
  // Distance in km
  const distanceKm = (steps * strideLength) / 1000;
  // Rough estimate: 0.5 kcal per kg per km walking
  return Math.round(distanceKm * weightKg * 0.5);
}

/**
 * Format date to locale string
 * @param dateStr - Date string in YYYY-MM-DD format
 * @param locale - Locale string (default: 'en-US')
 * @returns Formatted date string
 */
export function formatDate(dateStr: string, locale: string = 'en-US'): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Get week number from goal start date
 * @param startDateStr - Start date string
 * @param currentDateStr - Current date string
 * @returns Week number (1-indexed)
 */
export function getGoalWeekNumber(startDateStr: string, currentDateStr: string): number {
  const startDate = new Date(startDateStr);
  const currentDate = new Date(currentDateStr);
  const daysDiff = Math.floor((currentDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  return Math.floor(daysDiff / 7) + 1;
}

export default {
  calculateBMR,
  calculateTDEE,
  convertWeight,
  convertHeight,
  calculateGoalAdjustment,
  calculatePortionMacros,
  calculateTotalMacros,
  calculateWorkoutCalories,
  calculateWalkingCalories,
  formatDate,
  getGoalWeekNumber
};
